// components/ui/Popover.tsx — Glass-style popover for privacy selectors and tooltips

import * as RadixPopover from '@radix-ui/react-popover';
import type { ReactNode } from 'react';
import styles from './Popover.module.css';

interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  showArrow?: boolean;
  showClose?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Popover — glass-style floating panel for privacy tier selectors,
 * node tooltips, and contextual info. Wraps Radix Popover.
 */
export function Popover({
  trigger,
  children,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  showArrow = false,
  showClose = false,
  open,
  onOpenChange,
}: PopoverProps) {
  return (
    <RadixPopover.Root
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
    >
      <RadixPopover.Trigger asChild>
        {trigger}
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          className={styles.content}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={16}
        >
          {showClose && (
            <RadixPopover.Close className={styles.close} aria-label="Close">
              ✕
            </RadixPopover.Close>
          )}

          {children}

          {showArrow && <RadixPopover.Arrow className={styles.arrow} />}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
